import React, { useEffect, useState, useCallback, useMemo } from "react";
import { useNavigate, Link } from "react-router-dom";
import { videoService } from "../../services/videoService";
import { Folder } from "../../types";
import { FolderIcon, BookOpenIcon } from "@heroicons/react/24/outline";

const Dashboard: React.FC = () => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [folders, setFolders] = useState<Folder[]>([]);

  // Memoized folder click handler
  const handleFolderClick = useCallback(
    (folder: Folder) => {
      navigate(`/folder/${folder.id}`);
    },
    [navigate],
  );

  // Fetch data
  const fetchData = useCallback(async () => {
    try {
      setIsLoading(true);
      // Fetch folders
      const allFolders = await videoService.getFolders();

      // Filter for root folders (parent_id is null) and sort by sort_order
      const rootFolders = (allFolders || [])
        .filter((f) => f.parent_id === null)
        .sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0));

      setFolders(rootFolders);
    } catch (error) {
      console.error("Error fetching dashboard data:", error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Reset to first page when folders change
  // useEffect(() => {
  //   setCurrentPage(1);
  // }, [folders]);

  // Memoized folder cards
  const folderCards = useMemo(() => {
    return folders.map((folder) => {
      return (
        <div
          key={folder.id}
          onClick={() => handleFolderClick(folder)}
          className="group relative bg-white rounded-lg shadow-md overflow-hidden transition-all duration-300 flex flex-col justify-center items-center h-20 sm:h-24 cursor-pointer hover:shadow-lg transform hover:-translate-y-1 hover:scale-105"
        >
          <div className="w-full h-full flex items-center justify-center relative bg-gradient-to-r from-purple-500 to-indigo-600">
            <h3
              className="text-sm sm:text-base font-bold text-white text-center truncate w-full px-2"
              title={folder.name}
            >
              {folder.name}
            </h3>

            <div className="absolute inset-0 bg-black bg-opacity-20 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center">
              <div className="bg-white bg-opacity-90 rounded-full p-1">
                <BookOpenIcon className="h-4 w-4 text-indigo-600" />
              </div>
            </div>
          </div>
        </div>
      );
    });
  }, [folders, handleFolderClick]);

  return (
    <div className="py-2 sm:py-4">
      {/* Reduced Container Padding: px-2 */}
      <div className="px-1 sm:px-2">
        {/* Folder Cards Section */}
        {isLoading ? (
          <div className="mb-4">
            <div className="bg-white rounded-lg sm:rounded-xl shadow-lg p-6 border border-indigo-100 text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-4 border-indigo-200 border-t-indigo-600 mx-auto mb-3"></div>
              <p className="text-gray-600 text-base">ກຳລັງໂຫຼດ...</p>
            </div>
          </div>
        ) : folders.length > 0 ? (
          <div className="mb-4">
            {/* Header with folder count */}
            <div className="bg-white rounded-lg sm:rounded-xl shadow-lg p-3 sm:p-5 border border-indigo-100 mb-3">
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
                <div className="flex items-center">
                  <div className="bg-gradient-to-r from-indigo-500 to-purple-600 rounded-full p-2 shadow-lg mr-3">
                    <FolderIcon className="h-5 w-5 text-white" />
                  </div>
                  <div>
                    <h6 className="text-sm sm:text-xl font-bold text-gray-900">
                      ບົດຮຽນທັງຫມົດ {folders.length} ລາຍການ
                    </h6>
                    <p className="text-xs sm:text-sm text-gray-600 mt-0.5">
                      ເລືອກໂຟນເດີທີ່ທ່ານຕ້ອງການສຶກສາ
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 gap-2 sm:gap-4">
              {folderCards}
            </div>
          </div>
        ) : (
          <div className="mb-4">
            <div className="bg-white rounded-lg sm:rounded-xl shadow-lg p-6 border border-indigo-100 text-center">
              <div className="bg-gradient-to-r from-indigo-500 to-purple-600 rounded-full p-3 w-12 h-12 mx-auto mb-3 flex items-center justify-center">
                <FolderIcon className="h-6 w-6 text-white" />
              </div>
              <h3 className="text-base sm:text-xl font-bold text-gray-900 mb-1">
                ບໍ່ມີໂຟນເດີ
              </h3>
              <p className="text-xs text-gray-600">
                ຍັງບໍ່ມີການສ້າງໂຟນເດີເທື່ອ
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Dashboard;
